import type { ProcessStep, ProcessSubstep } from '@/modules/processes/types';

/**
 * Step ids are the addressing scheme for everything that points at part of a
 * process — issue capture today, acknowledgements and automations later. They
 * are generated once and never regenerated, so `forkSteps` copies them verbatim
 * and reordering only changes array position.
 */
export function newStepId(): string {
  return `s_${crypto.randomUUID().replace(/-/g, '').slice(0, 10)}`;
}

export function emptySubstep(): ProcessSubstep {
  return { id: newStepId(), title: '', detailMd: '' };
}

export function emptyStep(): ProcessStep {
  return { ...emptySubstep(), conditionMd: null, substeps: [] };
}

/**
 * The steps a new draft starts from. A deep copy so editing the draft cannot
 * mutate the published version's array in place, but ids are carried across
 * unchanged: a step that survives into the next version is the same step, and
 * "what changed between v2 and v3" is answered by comparing id arrays.
 */
export function forkSteps(steps: ProcessStep[]): ProcessStep[] {
  return steps.map((s) => ({
    ...s,
    substeps: s.substeps.map((sub) => ({ ...sub })),
  }));
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function substepFromJson(v: any): ProcessSubstep {
  return {
    id: typeof v?.id === 'string' && v.id ? v.id : newStepId(),
    title: asString(v?.title),
    detailMd: asString(v?.detail_md ?? v?.detailMd),
  };
}

/** Defensive: `steps` is jsonb, so nothing in the database guarantees its shape. */
export function stepsFromJson(value: unknown): ProcessStep[] {
  if (!Array.isArray(value)) return [];
  return value.map((raw: any) => ({
    ...substepFromJson(raw),
    conditionMd: typeof raw?.condition_md === 'string'
      ? raw.condition_md
      : typeof raw?.conditionMd === 'string'
        ? raw.conditionMd
        : null,
    substeps: Array.isArray(raw?.substeps) ? raw.substeps.map(substepFromJson) : [],
  }));
}

export function stepsToJson(steps: ProcessStep[]): unknown {
  return steps.map((s) => ({
    id: s.id,
    title: s.title,
    detail_md: s.detailMd,
    condition_md: s.conditionMd,
    substeps: s.substeps.map((sub) => ({
      id: sub.id,
      title: sub.title,
      detail_md: sub.detailMd,
    })),
  }));
}

/** Clamped rather than wrapping: moving the first step up is a no-op, not a jump to the end. */
export function moveByIndex<T>(list: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
}

export function updateStep(
  steps: ProcessStep[],
  stepId: string,
  patch: Partial<Omit<ProcessStep, 'id' | 'substeps'>>,
): ProcessStep[] {
  return steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s));
}

export function removeStep(steps: ProcessStep[], stepId: string): ProcessStep[] {
  return steps.filter((s) => s.id !== stepId);
}

export function addSubstep(steps: ProcessStep[], stepId: string): ProcessStep[] {
  return steps.map((s) => (s.id === stepId ? { ...s, substeps: [...s.substeps, emptySubstep()] } : s));
}

export function updateSubstep(
  steps: ProcessStep[],
  stepId: string,
  substepId: string,
  patch: Partial<ProcessSubstep>,
): ProcessStep[] {
  return steps.map((s) =>
    s.id === stepId
      ? { ...s, substeps: s.substeps.map((sub) => (sub.id === substepId ? { ...sub, ...patch } : sub)) }
      : s,
  );
}

export function removeSubstep(steps: ProcessStep[], stepId: string, substepId: string): ProcessStep[] {
  return steps.map((s) =>
    s.id === stepId ? { ...s, substeps: s.substeps.filter((sub) => sub.id !== substepId) } : s,
  );
}

export function moveSubstep(steps: ProcessStep[], stepId: string, index: number, delta: number): ProcessStep[] {
  return steps.map((s) => (s.id === stepId ? { ...s, substeps: moveByIndex(s.substeps, index, delta) } : s));
}

/** Every id in a version, for uniqueness checks and change comparison. */
export function allStepIds(steps: ProcessStep[]): string[] {
  return steps.flatMap((s) => [s.id, ...s.substeps.map((sub) => sub.id)]);
}
